/* eslint-disable multiline-ternary */
/* eslint-disable operator-linebreak */
/* eslint-disable no-unneeded-ternary */
import React, { CSSProperties, ReactElement, useRef, useState } from 'react';
import styles from './ListingTable.module.scss';
import { Property } from 'csstype';
import DateFormat from '../dateFormat/dateFormat';
import { useTranslation } from 'react-i18next';
import MoneyFormat from '../moneyFormat/MoneyFormat';
import { isTablet } from 'Config';
import { FaAngleDown, FaAngleUp, FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { IconType } from 'react-icons';
import HReafWrap from './hRefWrap/HRefWrap';

const SUBROWS_COLUMN_WIDTH = '10px';

export interface TableRow {
    [field: string]: any;
}

export type ListingTableCellFormat = 'default' | 'money' | 'date';

export interface ListingTableColumn<TRow, TParentRow = unknown> {
    field?: keyof TRow;
    name?: string | ReactElement;
    renderCell?: (row: TRow, column: ListingTableColumn<TRow, TParentRow>, index: number, parentRow?: TParentRow, parentRowIndex?: number) => ReactElement;
    cellAlignment?: Property.TextAlign;
    cellStyle?: CSSProperties;
    columnCellClassName?: string;
    columnCellAlignment?: Property.TextAlign;
    width?: string | number;
    preventClick?: boolean;
    cellFormat?: ListingTableCellFormat;
    cellFormatUnitByField?: string;
    hideOn?: string[]; // 'sm','md','lg','xl'
    onSearch?: (field: string, isFilterAsc: boolean) => void;
    searchField?: string;
    onlyImageColumn?: boolean;
}

interface Props<TRow, TSubRow = any> {
    columns: ListingTableColumn<TRow>[];
    subColumns?: ListingTableColumn<TSubRow, TRow>[];
    rows: TRow[];
    allowHover?: boolean;
    onRowClick?: (row: TRow, index: number, event: any) => void;
    onHref?: (row: TRow) => string;
    onSubRowClick?: (row: TSubRow, index: number, event: any) => void;
    striped?: boolean;
    selectedRow?: TRow | null;
    initialSearch?: SearchItem | null;
    showErrors?: boolean;
    errors?: any[];
    rowStyle?: (row: TRow, col: ListingTableColumn<TRow>, rowIndex: number, colIndex: number) => CSSProperties;
    subRowStyle?: (row: TSubRow, col: ListingTableColumn<TSubRow, TRow>, rowIndex: number, colIndex: number) => CSSProperties;
    className?: string;
    headerClassName?: string;
    hideHeaderWhenNoData?: boolean;
    isExpanded?: (row: TRow) => boolean;
    onLoadSubRows?: (row: TRow) => TSubRow[];
    colorRow?: string;
    colorSubRow?: string;
    hasSubRows?: boolean;
    onSubRowsLoadMore?: (row: TRow, index: number) => void;
    canSubRowsLoadMore?: (row: TRow, index: number) => boolean;
    labelSubRowWithoutResults?: string;
    allowSubRowHover?: boolean;
}

export interface SearchItem {
    colField: string;
    isOrderAsc: boolean;
}

const ListingTable = <TRow extends TableRow, TSubRow extends TableRow>({
    columns, subColumns, rows, selectedRow, allowHover = false, striped = false, className, headerClassName,
    initialSearch = null, showErrors = false, errors = [], onRowClick, onHref, onSubRowClick, rowStyle, subRowStyle, hideHeaderWhenNoData, isExpanded,
    onLoadSubRows, colorRow, colorSubRow, hasSubRows, onSubRowsLoadMore, canSubRowsLoadMore, labelSubRowWithoutResults, allowSubRowHover = false
}: Props<TRow, TSubRow>) => {
    const { t } = useTranslation();

    const getCellWidthStyle = (col: ListingTableColumn<TRow>) => {
        if (!col.width) return {};
        return { width: col.width };
    }

    const getSubCellWidthStyle = (col: ListingTableColumn<TSubRow, TRow>) => {
        if (!col.width) return {};
        return { width: col.width };
    }

    const getColumnCellStyle = (col: ListingTableColumn<TRow>) => {
        return {
            textAlign: col.columnCellAlignment || 'left',
            ...getCellWidthStyle(col),
        }
    }

    const getRowCellStyle = (col: ListingTableColumn<TRow>, row: TRow, rowIndex: number, colIndex: number): CSSProperties => {
        return {
            textAlign: col.cellAlignment || 'left',
            ...getCellWidthStyle(col),
            ...(col.cellStyle || {}),
            ...(rowStyle ? rowStyle(row, col, rowIndex, colIndex) : {}),
        }
    }

    const getSubRowCellStyle = (col: ListingTableColumn<TSubRow, TRow>, row: TSubRow, rowIndex: number, colIndex: number): CSSProperties => {
        return {
            textAlign: col.cellAlignment || 'left',
            ...getSubCellWidthStyle(col),
            ...(col.cellStyle || {}),
            ...(subRowStyle ? subRowStyle(row, col, rowIndex, colIndex) : {}),
        }
    }

    const getRowCellValue = (row: TRow, col: ListingTableColumn<TRow>, index: number) => {
        if (col.renderCell) return col.renderCell(row, col, index);
        if (!col.field) return '';
        const value = row[col.field];
        if (col.cellFormat && col.cellFormat === 'money') {
            return <MoneyFormat value={value} suffix={col.cellFormatUnitByField ? row[col.cellFormatUnitByField] : null} />;
        }
        if (col.cellFormat && col.cellFormat === 'date') {
            return <DateFormat value={value} />;
        }
        return value;
    }

    const getSubRowCellValue = (row: TSubRow, col: ListingTableColumn<TSubRow, TRow>, index: number, parentRow: TRow, parentRowIndex: number) => {
        if (col.renderCell) return col.renderCell(row, col, index, parentRow, parentRowIndex);
        if (!col.field) return '';
        const value = row[col.field];
        if (col.cellFormat && col.cellFormat === 'money') {
            return <MoneyFormat value={value} suffix={col.cellFormatUnitByField ? row[col.cellFormatUnitByField] : null} />;
        }
        if (col.cellFormat && col.cellFormat === 'date') {
            return <DateFormat value={value} />;
        }
        return value;
    }

    // Track mousedown position so we can distinguish a real click from a
    // drag-to-select-text gesture. If the pointer moves more than a few pixels
    // between mousedown and click, OR the user has actually selected text,
    // we treat it as a selection and skip the row click.
    const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);
    const CLICK_DRAG_THRESHOLD_PX = 4;

    const onCellMouseDown = (event: React.MouseEvent) => {
        mouseDownPosRef.current = { x: event.clientX, y: event.clientY };
    }

    const isTextSelectionGesture = (event: React.MouseEvent): boolean => {
        const start = mouseDownPosRef.current;
        mouseDownPosRef.current = null;
        if (start) {
            const dx = Math.abs(event.clientX - start.x);
            const dy = Math.abs(event.clientY - start.y);
            if (dx > CLICK_DRAG_THRESHOLD_PX || dy > CLICK_DRAG_THRESHOLD_PX) return true;
        }
        const selection = typeof window !== 'undefined' ? window.getSelection() : null;
        if (selection && selection.toString().length > 0) return true;
        return false;
    }

    const onClick = (event: React.MouseEvent, row: TRow, col: ListingTableColumn<TRow>, index: number) => {
        if (col.preventClick) return;
        if (isTextSelectionGesture(event)) return;
        if (onRowClick) onRowClick(row, index, event);
    }

    const onSubClick = (event: React.MouseEvent, row: TSubRow, col: ListingTableColumn<TSubRow, TRow>, index: number) => {
        if (col.preventClick) return;
        if (isTextSelectionGesture(event)) return;
        if (onSubRowClick) onSubRowClick(row, index, event);
    }

    const onSearch = (col: ListingTableColumn<TRow>) => {
        if (col.onSearch) {
            if (!isSearchActive) {
                col.onSearch(col.searchField ?? '', true);
                setIsSearchActive({ colField: col.searchField ?? '', isOrderAsc: true });
            } else {
                col.onSearch(col.searchField ?? '', isSearchActive.colField === col.searchField ? !isSearchActive.isOrderAsc : true);
                setIsSearchActive({ colField: col.searchField ?? '', isOrderAsc: isSearchActive.colField === col.searchField ? !isSearchActive.isOrderAsc : true });
            }
        }
    }

    const [isSearchActive, setIsSearchActive] = useState<SearchItem | null>(initialSearch);
    const isMobile = isTablet(window.innerWidth);

    const getHideClass = (hideOn?: string[]) => {
        if (!hideOn) return '';
        if (hideOn.includes('xxl')) return styles.xxl;
        if (hideOn.includes('xl')) return styles.xl;
        if (hideOn.includes('lg')) return styles.lg;
        if (hideOn.includes('md')) return styles.md;
        if (hideOn.includes('sm')) return styles.sm;
        return '';
    };

    const renderActiveFilterIcon = () => (
        <span className="ml-1.5 text-primary">
            {isSearchActive && !isSearchActive.isOrderAsc && <FaSortUp className="inline-block w-3 h-3" />}
            {isSearchActive && isSearchActive.isOrderAsc && <FaSortDown className="inline-block w-3 h-3" />}
        </span>
    );

    const renderHasFilterIcon = (col: ListingTableColumn<TRow>) => (
        <span className="ml-1.5 text-slate-300">
            {col.onSearch && <FaSort className="inline-block w-3 h-3" />}
        </span>
    );

    const renderRowArrow = (Icon: IconType, rowIndex: number, row: TRow) => (
        <td
            key="col-arrow-row"
            style={{ width: SUBROWS_COLUMN_WIDTH }}
            className="px-2 py-3.5 border-b border-slate-100 text-slate-400"
            onMouseDown={onCellMouseDown}
            onClick={event => {
                if (isTextSelectionGesture(event)) return;
                if (onRowClick) onRowClick(row, rowIndex, event);
            }}
        >
            <Icon />
        </td>
    );

    const renderRow = (row: TRow, rowIndex: number) => (
        <tr
            key={`row-${rowIndex}`}
            style={{
                boxShadow: showErrors && errors[rowIndex] != null ? 'rgb(255 0 0) 0px 0px 0px 2px inset' : '',
                background: colorRow ?? ''
            }}
            className={`
                ${allowHover ? 'hover:bg-cyan-50/30 transition-colors duration-150 cursor-pointer' : ''}
                ${(selectedRow && (selectedRow.id === row.id || selectedRow.index === row.index)) ? 'bg-primary-50/40' : ''}
            `}
        >
            {hasSubRows && (isExpanded && isExpanded(row) ? renderRowArrow(FaAngleUp, rowIndex, row) : renderRowArrow(FaAngleDown, rowIndex, row))}
            {columns.map((col, colIndex) => (
                <td
                    key={`col-${colIndex}`}
                    onMouseDown={onCellMouseDown}
                    onClick={event => onClick(event, row, col, rowIndex)}
                    className={`
                        ${getHideClass(col.hideOn)}
                        px-4 py-3.5 text-sm text-slate-700 border-b border-slate-100
                        ${col.onlyImageColumn ? 'px-1' : ''}
                        ${striped && rowIndex % 2 === 0 ? 'bg-slate-50/50' : ''}
                        ${isMobile ? 'text-[0.8125rem]' : ''}
                    `}
                    style={getRowCellStyle(col, row, rowIndex, colIndex)}
                >
                    {!col.preventClick && <HReafWrap href={onHref ? onHref(row) : null}>{getRowCellValue(row, col, rowIndex)}</HReafWrap>}
                    {col.preventClick && getRowCellValue(row, col, rowIndex)}
                </td>
            ))}
        </tr>
    );

    const renderSubRow = (row: TSubRow, rowIndex: number, parentRow: TRow, parentRowIndex: number) => (
        <tr
            key={`subrow-${rowIndex}`}
            style={{
                boxShadow: showErrors && errors[rowIndex] != null ? 'rgb(255 0 0) 0px 0px 0px 2px inset' : '',
                background: colorSubRow ?? ''
            }}
            className={`
                ${allowSubRowHover ? 'hover:bg-cyan-50/30 transition-colors duration-150 cursor-pointer' : ''}
                ${(selectedRow && (selectedRow.id === row.id || selectedRow.index === row.index)) ? 'bg-primary-50/40' : ''}
            `}
        >
            {hasSubRows && (
                <td key="col-sb" style={{ width: SUBROWS_COLUMN_WIDTH }} className="px-2 py-2 border-b border-slate-100" />
            )}
            {(subColumns || []).map((col, colIndex) => (
                <td
                    key={`col-${colIndex}`}
                    onMouseDown={onCellMouseDown}
                    onClick={event => onSubClick(event, row, col, rowIndex)}
                    className={`
                        ${getHideClass(col.hideOn)}
                        px-4 py-2 text-sm text-slate-600 border-b border-slate-50
                        ${isMobile ? 'text-[0.8125rem]' : ''}
                    `}
                    style={getSubRowCellStyle(col, row, rowIndex, colIndex)}
                >
                    {getSubRowCellValue(row, col, rowIndex, parentRow, parentRowIndex)}
                </td>
            ))}
        </tr>
    );

    const renderSubRowLoadMore = (row: TRow, rowIndex: number) => (
        <tr
            key={`subrow-load-more-${rowIndex}`}
            className="hover:bg-primary-50/30 transition-colors cursor-pointer"
        >
            <td
                colSpan={(subColumns || []).length + 1}
                onClick={() => onSubRowsLoadMore && onSubRowsLoadMore(row, rowIndex)}
                className="px-4 py-2.5 text-sm text-primary font-medium bg-primary-50/20 text-center border-b border-slate-100"
            >
                {t('common.load_more')}
            </td>
        </tr>
    );

    const renderSubRowWithoutResults = (row: TRow, rowIndex: number) => (
        <tr key={`subrow-without-results-${rowIndex}`}>
            <td
                colSpan={(subColumns || []).length + 1}
                className="px-4 py-3 text-sm text-slate-400 text-center bg-slate-50/50 border-b border-slate-100"
            >
                {labelSubRowWithoutResults ? labelSubRowWithoutResults : t('common.no_results')}
            </td>
        </tr>
    );

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className={`w-full min-w-max border-collapse ${className || ''}`}>
                {(Boolean(rows.length > 0) || (Boolean(rows.length <= 0) && !hideHeaderWhenNoData)) && (
                    <thead>
                        <tr className="bg-slate-50/80 border-b border-slate-200">
                            {hasSubRows && <th style={{ width: SUBROWS_COLUMN_WIDTH }} />}
                            {columns.map((col, colIndex) => (
                                <th
                                    key={`col-${colIndex}`}
                                    className={`
                                        ${getHideClass(col.hideOn)}
                                        px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500
                                        ${col.onlyImageColumn ? 'px-1' : ''}
                                        ${col.onSearch ? 'cursor-pointer hover:text-slate-700 transition-colors' : ''}
                                        ${col.columnCellClassName || ''}
                                        ${headerClassName || ''}
                                    `}
                                    style={getColumnCellStyle(col)}
                                    onClick={() => onSearch(col)}
                                >
                                    <span className={`flex items-center whitespace-nowrap ${col.columnCellAlignment === 'right' ? 'justify-end' : 'justify-start'}`}>
                                        {col.name}
                                        {isSearchActive && isSearchActive.colField === col.searchField ? renderActiveFilterIcon() : renderHasFilterIcon(col)}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {rows.map((row, rowIndex) => {
                        const subrows = onLoadSubRows ? onLoadSubRows(row) : null;
                        const subRowsLoadMore = canSubRowsLoadMore && canSubRowsLoadMore(row, rowIndex);
                        return (
                            <React.Fragment key={'fragment-row-' + rowIndex}>
                                {renderRow(row, rowIndex)}
                                {isExpanded && isExpanded(row) && subrows && <>
                                    {subrows.map((subrow, subrowIndex) => renderSubRow(subrow, subrowIndex, row, rowIndex))}
                                    {subRowsLoadMore && renderSubRowLoadMore(row, rowIndex)}
                                </>}
                                {isExpanded && isExpanded(row) && subrows?.length === 0 && renderSubRowWithoutResults(row, rowIndex)}
                            </React.Fragment>
                        );
                    })}
                </tbody>
                {Boolean(rows.length <= 0) && (
                    <tfoot>
                        <tr>
                            <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-slate-400">
                                {t('messages.no_info_to_show')}
                            </td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}

export default ListingTable;
